import { action, computed, makeObservable, observable, toJS } from 'mobx';
import { FormEvent, MouseEvent } from 'react';
import { IMealItemEditModal } from '../types/mealItemEditModal';
import { modalStore } from '~/mobx/modalStore';
import { IMealItem } from '~/types/combo';
import { IProductV4 } from '~/types/product';
import { booleanNumberReverse } from '~/utils/helpers';
import { confirmZeroPrice } from '~/utils/modalConfirmations';
import { productUtils } from '~/utils/productUtils';
import { IPickerOption } from '~/view/components/Controls/Pickers/Picker/types';
import { MealItemEditAction } from '~/view/screens/MenuManagementScreen/screens/MealsScreen/constants/meals';

class MealItemEditModalStore {
  constructor() {
    makeObservable(this);
  }

  /** needed for correct operation of saving and deleting */
  @observable private initItem: IMealItemEditModal['item'] = undefined;
  private sizeId: IMealItemEditModal['sizeId'] = 0;
  private groupId: IMealItemEditModal['groupId'] = 0;
  private choiceId: IMealItemEditModal['choiceId'] = 0;
  private onSubmitHandler: IMealItemEditModal['onSubmit'] = () => {
    return;
  };
  private onDeleteHandler: IMealItemEditModal['onDelete'] = () => {
    return;
  };

  private fullItemsList: IProductV4[] = [];

  @observable public item: IMealItemEditModal['item'] = undefined;

  public get showOpenMealItemMdfExclModalButton(): boolean {
    if (!this.item) return false;
    return Boolean(this.item.is_modifiers);
    // TODO: combo exclusions
    // return Boolean(this.item.is_modifiers || this.item.is_exclusions);
  }

  public get isEditAction(): boolean {
    return Boolean(this.initItem);
  }

  @computed public get modalHeader(): string {
    return this.isEditAction ? 'edit_or_del_cmgci' : 'add_item';
  }

  public initStore = (props: IMealItemEditModal) => {
    this.initItem = toJS(props.item);

    this.sizeId = props.sizeId;
    this.groupId = props.groupId;
    this.choiceId = props.choiceId;
    this.onSubmitHandler = props.onSubmit;
    this.onDeleteHandler = props.onDelete;

    this.item = toJS(props.item);
  };

  public onSetFullItemsList = (list: IProductV4[]) => {
    this.fullItemsList = list;
  };

  @action public onSelectItem = (options: IPickerOption[]) => {
    if (options.length) {
      const item: IProductV4 | undefined = this.fullItemsList.find((_item) => _item.id === options[0].value);
      if (item) this.item = productUtils.transformToMealItem(item, this.sizeId, this.groupId, this.choiceId);

      return;
    }

    this.item = undefined;
  };

  @action public onChangePrice = (price: string | undefined) => {
    if (!this.item) return;

    this.item.price = price || '';
  };

  @action public onChangeDefault = () => {
    if (!this.item) return;

    this.item.is_default = booleanNumberReverse(this.item.is_default);
  };

  public onOpenMealItemMdfExclModal = () => {
    if (!this.item) return;

    modalStore.openMealItemMdfExclModal({ item: this.item });
  };

  @action public onSubmit = (event: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const zeroPrice: boolean = Boolean(this.item && Number(this.item.price) === 0);
    if (zeroPrice) {
      confirmZeroPrice({ onConfirm: this._onSubmit });
      return;
    }

    this._onSubmit();
  };
  private _onSubmit = () => {
    const actionType: MealItemEditAction = this.isEditAction ? MealItemEditAction.EDIT : MealItemEditAction.ADD;
    const item: IMealItem | undefined = this.item ? { ...this.item, price: this.item.price || '0' } : undefined;

    if (!item) {
      this.onDelete();
      return;
    }

    this.onSubmitHandler(actionType, item);
    this.onCloseModal();
  };

  @action public onDelete = () => {
    if (this.initItem) this.onDeleteHandler(MealItemEditAction.DELETE, this.initItem);

    this.onCloseModal();
  };

  @action public onCloseModal = () => {
    modalStore.closeMealItemEditModal();
  };
}

export const mealItemEditModalStore = new MealItemEditModalStore();
